import React, { useMemo } from 'react';
import {
  Box,
  Flex,
  Text,
  useColorModeValue,
  useToken,
} from '@chakra-ui/react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  LabelList,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { useBrandPalette } from '../../../contexts/BrandPaletteContext';
import { spectrumColors } from '../../../theme/colors';
import { HubGhostButton, HubPanel, hub } from './hubUi';

const FILL_CLASSIC = ['#7A2180', '#005A9C', '#009E5F', '#E0441C', '#3E8CC4'];
const FILL_SPECTRUM = [
  spectrumColors.diversity,
  spectrumColors.logistics,
  spectrumColors.resources,
  spectrumColors.energy,
  '#3E8CC4',
  '#681C6C',
  '#004A82',
];

function chartFills(paletteId) {
  if (paletteId === 'spectrum') return FILL_SPECTRUM;
  if (paletteId === 'logoBrand') return ['#1E7AD8', '#E87B20', '#8E278F', '#0B5CAD'];
  if (paletteId === 'matchClarity') return ['#2C6ECB', '#008060', '#5C6AC4', '#9C6ADE'];
  return FILL_CLASSIC;
}

function ChartTooltip({ active, payload }) {
  if (!active || !payload?.length) return null;
  const row = payload[0]?.payload;
  if (!row) return null;
  return (
    <Box
      bg="bg.surface"
      borderWidth="1px"
      borderColor="border.subtle"
      borderRadius="10px"
      px={3}
      py={2}
      boxShadow="sm"
    >
      <Text fontSize="13px" fontWeight="700" color="text.primary">
        {row.buName}
      </Text>
      <Text fontSize="12px" color="text.secondary">
        {row.count} active assignment{row.count === 1 ? '' : 's'}
      </Text>
    </Box>
  );
}

/**
 * Horizontal chart: active indent deployments (assigned, not released)
 * by demand business unit. Data: GET /dashboard → equipment.byBusinessUnitDeployed.
 */
export default function HubBuDeployedChart({
  byBusinessUnit = [],
  onOpenIndents,
}) {
  const { paletteId } = useBrandPalette();
  const fills = useMemo(() => chartFills(paletteId), [paletteId]);
  const [axisToken] = useToken('colors', ['text.secondary']);
  const axisFallback = useColorModeValue('#5C6570', '#A8A49A');
  const axisColor = axisToken || axisFallback;
  const gridStroke = useColorModeValue('#E8ECF0', '#2A3038');

  const rows = useMemo(() => {
    return (Array.isArray(byBusinessUnit) ? byBusinessUnit : [])
      .map((row, i) => ({
        buName: row.buName || 'Unspecified',
        count: Number(row.count) || 0,
        fill: fills[i % fills.length],
      }))
      .filter((r) => r.count > 0);
  }, [byBusinessUnit, fills]);

  const chartH = Math.max(240, Math.min(520, Math.max(rows.length, 3) * 36 + 56));
  const xMax = Math.max(1, ...rows.map((r) => r.count), 1);

  return (
    <HubPanel minW={0} w="100%" overflow="hidden">
      <Flex
        align={{ base: 'stretch', md: 'center' }}
        justify="space-between"
        gap={3}
        mb={4}
        w="100%"
        flexWrap="wrap"
      >
        <Text fontWeight="600" fontSize="15px" color="text.primary">
          Business unit · Deployed
        </Text>
        {onOpenIndents ? (
          <HubGhostButton
            size="xs"
            onClick={onOpenIndents}
            sx={{
              h: '28px',
              minH: '28px',
              px: 2.5,
              fontSize: '12px',
              fontWeight: '600',
              borderRadius: 'full',
            }}
          >
            Open indents →
          </HubGhostButton>
        ) : null}
      </Flex>

      {rows.length === 0 ? (
        <Flex
          align="center"
          justify="center"
          minH="180px"
          borderRadius="12px"
          bg={hub.softBg}
        >
          <Text fontSize="14px" color={hub.muted}>
            No active deployments by business unit
          </Text>
        </Flex>
      ) : (
        <Box w="100%" minW={0} h={`${chartH}px`}>
          <ResponsiveContainer width="100%" height="100%" minWidth={0}>
            <BarChart
              data={rows}
              layout="vertical"
              margin={{ top: 4, right: 36, bottom: 12, left: 8 }}
              barCategoryGap="18%"
            >
              <CartesianGrid
                horizontal={false}
                stroke={gridStroke}
                strokeDasharray="3 6"
              />
              <XAxis
                type="number"
                domain={[0, Math.ceil(xMax * 1.08)]}
                allowDecimals={false}
                axisLine={false}
                tickLine={false}
                tick={{ fill: axisColor, fontSize: 11 }}
                label={{
                  value: 'Assigned deployments',
                  position: 'insideBottom',
                  offset: -4,
                  style: { fill: axisColor, fontSize: 11 },
                }}
              />
              <YAxis
                type="category"
                dataKey="buName"
                width={110}
                axisLine={false}
                tickLine={false}
                tick={{ fill: axisColor, fontSize: 12, fontWeight: 600 }}
              />
              <Tooltip
                cursor={{ fill: gridStroke, opacity: 0.35 }}
                content={<ChartTooltip />}
              />
              <Bar
                dataKey="count"
                radius={[0, 8, 8, 0]}
                maxBarSize={28}
                background={{ fill: gridStroke, opacity: 0.35, radius: 8 }}
              >
                {rows.map((row) => (
                  <Cell key={row.buName} fill={row.fill} />
                ))}
                <LabelList
                  dataKey="count"
                  position="right"
                  style={{
                    fill: axisColor,
                    fontSize: 12,
                    fontWeight: 700,
                  }}
                />
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </Box>
      )}
    </HubPanel>
  );
}
